// Atlas Dashboard — Conversations Page
// Lists all email conversations with state badges and filtering.

import { useState } from "react";
import { MessageCircle, Filter, Clock, CheckCircle, AlertTriangle, Circle } from "lucide-react";
import { useConversations } from "../hooks/useApi";
import { formatIstDate, truncate } from "../lib/format";
import { CONVERSATION_STATE_CONFIG } from "@atlas/shared";
import type { Conversation, ConversationState } from "@atlas/shared";

export function ConversationsPage() {
  const [stateFilter, setStateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const { data, loading, error } = useConversations(page, stateFilter);

  if (loading) return <div style={{ padding: "48px", textAlign: "center", color: "var(--color-text-tertiary)" }}>Loading conversations...</div>;
  if (error) return <div style={{ padding: "48px", textAlign: "center", color: "#DC2626" }}>{error}</div>;
  if (!data || data.items.length === 0) return <EmptyState />;

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px", alignItems: "center" }}>
        <Filter size={16} color="var(--color-text-tertiary)" />
        {["all", "waiting_for_reply", "reply_received", "no_reply_overdue", "ended"].map((s) => (
          <FilterButton
            key={s}
            label={s === "all" ? "All" : CONVERSATION_STATE_CONFIG[s as ConversationState]?.label || s}
            active={stateFilter === s}
            onClick={() => { setStateFilter(s); setPage(1); }}
          />
        ))}
      </div>

      {/* Conversation list */}
      <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "1px solid var(--color-border)" }}>
              <th style={thStyle}>Subject</th>
              <th style={thStyle}>Messages</th>
              <th style={thStyle}>State</th>
              <th style={thStyle}>Last Activity</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((conv: Conversation) => (
              <ConvRow key={conv.conversationId} conv={conv} />
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "16px", fontSize: "13px", color: "var(--color-text-tertiary)" }}>
        {data.total} conversations • Page {data.page}
      </div>
    </div>
  );
}

function ConvRow({ conv }: { conv: Conversation }) {
  const stateConfig = CONVERSATION_STATE_CONFIG[conv.state as ConversationState];
  const StateIcon = conv.state === "reply_received" ? CheckCircle : conv.state === "no_reply_overdue" ? AlertTriangle : conv.state === "ended" ? Circle : Clock;
  const stateColor = stateConfig?.color === "green" ? "#16A34A" : stateConfig?.color === "amber" ? "#D97706" : stateConfig?.color === "blue" ? "#2563EB" : "#6B7280";

  return (
    <tr
      style={{ borderBottom: "1px solid var(--color-border)", cursor: "pointer", transition: "background 0.15s" }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <td style={tdStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {conv.isPriority === true && <span style={{ color: "#D97706", fontSize: "12px" }}>★</span>}
          {truncate(conv.subject, 50)}
        </div>
      </td>
      <td style={tdStyle}>{conv.messageCount}</td>
      <td style={tdStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <StateIcon size={14} color={stateColor} />
          <span style={{ fontSize: "13px" }}>{stateConfig?.label}</span>
        </div>
      </td>
      <td style={{ ...tdStyle, color: "var(--color-text-tertiary)", fontSize: "13px" }}>{formatIstDate(conv.lastActivityAt)}</td>
    </tr>
  );
}

const thStyle: React.CSSProperties = { textAlign: "left", padding: "12px 16px", fontSize: "12px", fontWeight: 600, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.5px" };
const tdStyle: React.CSSProperties = { padding: "12px 16px", color: "var(--color-text-primary)" };

function FilterButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ padding: "6px 12px", borderRadius: "6px", border: `1px solid ${active ? "#2563EB" : "var(--color-border)"}`, backgroundColor: active ? "#EFF6FF" : "transparent", color: active ? "#2563EB" : "var(--color-text-secondary)", fontSize: "13px", fontWeight: active ? 600 : 400, cursor: "pointer" }}>
      {label}
    </button>
  );
}

function EmptyState() {
  return (
    <div style={{ padding: "48px", textAlign: "center" }}>
      <MessageCircle size={48} style={{ color: "var(--color-text-tertiary)", margin: "0 auto 16px" }} />
      <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>No conversations yet</h3>
      <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>
        Once you send tracked emails and receive replies, your conversations will appear here.
      </p>
    </div>
  );
}
